/**
 * NotesChartTab — Notes inner tab of the patient Chart shell
 * (PROD-738 PR C, read-only). View / Edit SOAP body ships on the
 * encounter detail screen (encounter epic — out of scope here).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/notes_tab.dart`.
 *
 * The "Notes" label is a Flutter UX choice — the wire returns
 * ENCOUNTERS, not freestanding note records. Each row is a
 * chronological visit; the title is the chief complaint
 * (`reasonForVisitName`). Status pill maps the encounter lifecycle
 * (in-progress / finished / cancelled). Date column shows when the
 * visit happened, NOT when the record was created.
 */

import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useState, useEffect } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { usePatientNotes } from '@/patients/services/use-patient-notes';
import {
  encounterStatusLabel,
  isLockedEncounter,
} from '@/patients/lib/encounter-status';
import { ChartSectionHeader } from './chart-section-header';
import { PostVisitSummaryDialog } from './post-visit-summary-dialog';
import { ClinicalNotePanel } from '@/billing/components/claim-review/clinical-note-panel';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { PatientEncounterNote } from '@/types/patient-chart-api';
import type { Patient } from '@/types/patient-api';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

// Flutter `notes_tab.dart` fetches pageSize 50.
const PAGE_SIZE = 50;

export interface NotesChartTabProps {
  patientId: string | null | undefined;
  /**
   * Chart patient — supplies the demographics (name / MRN / DOB / age /
   * gender) the clinical-note viewer header renders. Optional so the
   * component still mounts before the patient resolves.
   */
  patient?: Patient | null;
}

export function NotesChartTab({ patientId, patient }: NotesChartTabProps) {
  const [page, setPage] = useState(1);
  const [viewingNote, setViewingNote] = useState<PatientEncounterNote | null>(
    null,
  );
  // PVS dialog state, kept at the tab level so the clinical-note viewer
  // stays free of PVS-specific wiring — the "View PVS" header action just
  // hands the encounter id back up here (PROD-874).
  const [pvsEncounterId, setPvsEncounterId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [patientId]);

  const { data, isFetching, isError, refetch } = usePatientNotes({
    patientId,
    pageNumber: page,
    pageSize: PAGE_SIZE,
  });

  const rows = (data ?? []).filter((n) => n.isDeleted !== true);
  const hasMore = (data?.length ?? 0) === PAGE_SIZE;
  // The first 5 notes, "Show all N" for the page's rest (N: the notes of this page of 50); the pager waits behind it.
  const cap = useRowCap(rows);
  useReportChartCount(isFetching && rows.length === 0 ? null : rows.length);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to view notes."
          />
      </div>
    );
  }

  if (isFetching && rows.length === 0) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load notes."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
            icon="file"
            title="No notes recorded for this patient yet."
            description="Each completed encounter appears here once SOAP documentation is saved."
          />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-3">
      {/* Notes are authored from the encounter SOAP flow — no chart-level add. */}
      <ChartSectionHeader title="Notes" count={rows.length} showAdd={false} />
      {/* The design's NotesChartTab table (export 21-10 chart-notes.html: Date · Type · Author · Status); the PA keeps
          its View action as the last column. Summary has no feed in the notes read. */}
      <div className="chart-plain-table" data-slot="chart-list">
        <table className="w-full">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Author</th>
              <th>Status</th>
              <th className="chart-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cap.visible.map((n, i) => (
              <NoteRow
                key={n.identifier ?? `${page}-${i}`}
                note={n}
                onView={() => setViewingNote(n)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <ChartShowAll cap={cap} label="notes" />

      {/* View → the shared clinical-note viewer (the same screen used by
          "view claims"), with Print enabled per Flutter `notes_tab.dart`
          (`showEncounterDetail` → Print button). The encounter id is the
          note's `identifier`; demographics come from the chart patient. */}
      <ClinicalNotePanel
        open={viewingNote !== null}
        onOpenChange={(open) => {
          if (!open) setViewingNote(null);
        }}
        encounterId={viewingNote?.identifier ?? undefined}
        patientId={patientId ?? undefined}
        centred={isEncounterFlagOn('layoutFixes')}
        patient={
          patient
            ? {
                name:
                  [patient.firstName, patient.middleName, patient.lastName]
                    .filter(Boolean)
                    .join(' ') || undefined,
                mrn: patient.patientMrn ?? undefined,
                birthDate: patient.birthDate ?? undefined,
                age: patient.age ?? undefined,
                genderName: patient.genderName?.name ?? undefined,
              }
            : undefined
        }
        provider={
          viewingNote?.performerDetails
            ? {
                name: viewingNote.performerDetails.name,
                imageUrl: viewingNote.performerDetails.photo,
              }
            : undefined
        }
        encounterDate={viewingNote?.startDate ?? undefined}
        showPrint
        headerExtra={
          viewingNote?.identifier ? (
            <button
              type="button"
              onClick={() => {
                // Close the note viewer before opening PVS so the two don't
                // stack — same flow as Flutter `openPvs()`.
                const id = viewingNote.identifier!;
                setViewingNote(null);
                setPvsEncounterId(id);
              }}
              className="chart-link"
            >
              View PVS
            </button>
          ) : null
        }
      />

      <PostVisitSummaryDialog
        open={pvsEncounterId !== null}
        onOpenChange={(open) => {
          if (!open) setPvsEncounterId(null);
        }}
        encounterId={pvsEncounterId}
      />

      {page > 1 || !cap.capped || cap.expanded ? (
      <div className="flex items-center justify-between text-sm text-text-muted pt-1">
        <span>
          Showing page {page}
          {hasMore ? '' : ' (end)'}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || isFetching}
          >
            Next
          </Button>
        </div>
      </div>
      ) : null}
    </div>
  );
}

function NoteRow({
  note,
  onView,
}: {
  note: PatientEncounterNote;
  onView: () => void;
}) {
  // Status label/tone + View gate are centralised in `encounter-status.ts`
  // (verbatim Flutter `notes_tab.dart:141-152`): Completed → "Locked",
  // Draft → "Draft", else "Ongoing"; View shows on Locked only.
  const statusLabel = encounterStatusLabel(note.status);
  const isLocked = isLockedEncounter(note.status);

  const provider = note.performerDetails?.name?.trim();
  const speciality = note.speciality?.display?.trim();
  const type = titleCase(note.reasonForVisitName) || 'Encounter';

  return (
    <tr data-slot="chart-list-row">
      <td data-chart-cell="date">{formatNoteDateTime(note.startDate ?? note.createdDate)}</td>
      <td data-chart-cell="name" title={type}>
        {type}
      </td>
      <td data-chart-cell="text">
        {provider || '—'}
        {speciality ? `, ${speciality}` : ''}
        {note.createdDate ? <div className="chart-cell-sub">Created on {formatNoteDate(note.createdDate)}</div> : null}
      </td>
      <td data-chart-cell="short">
        <EncounterStatusPill label={statusLabel} />
      </td>
      <td data-chart-cell="actions" className="chart-td-actions">
        {isLocked ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onView}
            aria-label={`View encounter: ${note.reasonForVisitName ?? 'Encounter'}`}
          >
            View
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/** Encounter-lifecycle status as the one soft status chip (design rules, "Status": never a solid pill; it was a
 *  solid green / amber / grey pill in 12 bold, Flutter `IndicatorTagWidget`). Locked reads done (green), Draft is the
 *  hollow dashed draft chip, Ongoing grey. Same labels, same rule (`encounter-status.ts`). */
function EncounterStatusPill({ label }: { label: string }) {
  const semantic = label === 'Locked' ? 'success' : 'neutral';
  return (
    <StatusChip tone="soft" semantic={semantic} data-chip-look={label === 'Draft' ? 'draft' : undefined}>
      {label}
    </StatusChip>
  );
}

/** Title-case each word. (Flutter's `String.toCamelCase()` is misnamed — it
 *  capitalises the first letter of each word, i.e. title case: "surgery" →
 *  "Surgery", "reason for visit" → "Reason For Visit". This is the matching
 *  title-case helper, named for what it does.) */
function titleCase(s: string | null | undefined): string {
  if (!s?.trim()) return '';
  return s
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** "May 14, 2026 9:19p" — Flutter `scDateTimeFormat`. Single-letter am/pm. */
function formatNoteDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const hour12 = h % 12 || 12;
  return `${date} ${hour12}:${m}${h >= 12 ? 'p' : 'a'}`;
}

/** "May 14, 2026" — Flutter `scDateFormat`. */
function formatNoteDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
